import {
    Children,
    type MouseEventHandler,
    type ReactNode,
    type Ref,
} from "react";
import classNames from "classnames";
import { TagCloseIcon } from "./TagCloseIcon";
import styles from "./Tag.module.scss";

// Visual source of truth: docs/specs/2026-08-28-tag-figma-rework.md.
// The prop unions encode the Figma component set literally — combinations
// the set doesn't contain must not compile (spec "Matrix typing" decision).

type TagColor = "accent" | "primary" | "neutral" | "error";

interface TagCommonProps {
    outline?: boolean;
    className?: string;
    children: ReactNode;
    ref?: Ref<HTMLElement>;
    "data-testid"?: string;
    /** Playground-only: statically render an interaction state. */
    "data-forced-state"?: "hover" | "hoverX";
}

interface TagInteractionProps {
    /**
     * Renders the root as a real button. With onRemove, the label becomes its
     * own button beside the X instead, so the two never nest.
     */
    onClick?: MouseEventHandler<HTMLButtonElement>;
    /** Renders the built-in X button. */
    onRemove?: MouseEventHandler<HTMLButtonElement>;
    /** Accessible name for the X button. */
    removeLabel?: string;
}

type WithClick = Pick<TagInteractionProps, "onClick"> & {
    onRemove?: never;
    removeLabel?: never;
};

type MdTag = { size?: "md"; color?: TagColor } & (
    | ({ leftIcon?: ReactNode; numeric?: never } & TagInteractionProps)
    | ({ leftIcon?: never; numeric?: boolean } & WithClick)
);

type LgTag = { size: "lg" } & (
    | ({
          color?: "primary" | "accent";
          leftIcon?: ReactNode;
          numeric?: never;
      } & TagInteractionProps)
    | ({ color?: "primary"; leftIcon?: never; numeric: true } & WithClick)
);

type SmTag = { size: "sm"; color?: TagColor } & (
    | ({ leftIcon?: ReactNode; numeric?: never } & WithClick)
    | ({ leftIcon?: never; numeric: true } & WithClick)
);

type XsTag = {
    size: "xs";
    color?: TagColor;
    leftIcon?: never;
    numeric: true;
} & WithClick;

export type TagProps = TagCommonProps & (MdTag | LgTag | SmTag | XsTag);

const sizeClasses = {
    xs: styles.sizeXs,
    sm: styles.sizeSm,
    md: styles.sizeMd,
    lg: styles.sizeLg,
};

const colorClasses = {
    accent: styles.accent,
    primary: styles.primary,
    neutral: styles.neutral,
    error: styles.error,
};

/**
 * Whether the label shows text of its own. CSS cannot see text nodes, so this
 * marks the label for the avatar padding rules; elements (an avatar, a visually
 * hidden name) don't count.
 */
const hasOwnText = (children: ReactNode) =>
    Children.toArray(children).some((child) =>
        typeof child === "string"
            ? child.trim() !== ""
            : typeof child === "number",
    );

/** Selects a Tag's text label, for callers that measure whether it is cut off. */
export const TAG_TEXT_LABEL_SELECTOR = "[data-has-text]";

const isObjectRef = (
    candidate: Ref<HTMLElement>,
): candidate is { current: HTMLElement | null } =>
    typeof candidate === "object" && candidate !== null;

export const Tag = ({
    size = "md",
    color: colorProp,
    outline = false,
    numeric = false,
    leftIcon,
    onClick,
    onRemove,
    removeLabel = "Remove",
    className,
    children,
    ref,
    "data-testid": dataTestid,
    "data-forced-state": forcedState,
}: Readonly<TagProps>) => {
    const color = colorProp ?? (size === "lg" ? "primary" : "accent");
    const isInteractive = onClick !== undefined || onRemove !== undefined;
    const assignButtonRef = (node: HTMLButtonElement | null) => {
        if (typeof ref === "function") {
            ref(node);
        } else if (ref && isObjectRef(ref)) {
            ref.current = node;
        }
    };
    const rootClassName = classNames(
        styles.tag,
        colorClasses[color],
        sizeClasses[size],
        {
            [styles.outline]: outline,
            [styles.numeric]: numeric,
            [styles.interactive]: isInteractive,
        },
        className,
    );
    const labelContent = (
        <>
            {leftIcon !== undefined && (
                <span className={styles.slot}>{leftIcon}</span>
            )}
            <span
                className={styles.label}
                data-has-text={hasOwnText(children) ? "" : undefined}
            >
                {children}
            </span>
        </>
    );
    const xButton = onRemove !== undefined && (
        <button
            type="button"
            className={styles.xButton}
            aria-label={removeLabel}
            onClick={onRemove}
        >
            <TagCloseIcon />
        </button>
    );
    const content = (
        <>
            {labelContent}
            {xButton}
        </>
    );

    if (onClick !== undefined && onRemove !== undefined) {
        return (
            <span
                ref={ref}
                className={rootClassName}
                data-testid={dataTestid}
                data-forced-state={forcedState}
            >
                <button
                    type="button"
                    className={styles.labelButton}
                    onClick={onClick}
                >
                    {labelContent}
                </button>
                {xButton}
            </span>
        );
    }

    if (onClick !== undefined) {
        return (
            <button
                type="button"
                ref={assignButtonRef}
                className={rootClassName}
                onClick={onClick}
                data-testid={dataTestid}
                data-forced-state={forcedState}
            >
                {content}
            </button>
        );
    }

    return (
        <span
            ref={ref}
            className={rootClassName}
            data-testid={dataTestid}
            data-forced-state={forcedState}
        >
            {content}
        </span>
    );
};
